import type {
  ExcelUploadGroupedResult,
  ExcelUploadOrderGroup,
  ExcelUploadValidationResult,
  FailedRow,
  ParsedExcelRow,
  TemplateMetadata,
  ValidatedRow,
} from '@/features/excel-order-upload/types/excel-template-upload.types';
import type { MatrixProduct } from '@/models/matrix-product.model';
import { ProductsMatrixService } from '@/services/products-matrix.service';
import { useProductMatrixStore } from '@/stores/product-matrix';
import { useUserStore } from '@/stores/user';
import type { ProductMatrixApiResponse } from '@/types/matrix.interface';
import { isDateAfter, isDateBefore } from '@/utils/temporalUtils';
import { getDistributorSeasonWindow, MatrixType } from '@onrunning/backstage-models';
import { computed, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFabricValidation } from '../../../composables/useFabricValidation';
import { DISTRIBUTOR_MOQ, MAX_QUANTITY } from '../constants/excel-template.constants';

export function useExcelUploadValidation(matrixTypeRef: Ref<MatrixType | undefined>) {
  const { t } = useI18n();
  const userStore = useUserStore();
  const productMatrixStore = useProductMatrixStore();
  const productsMatrixService = new ProductsMatrixService();

  const matrixTypeComputed = computed(() => matrixTypeRef.value);
  const { getFabricDisabledReason } = useFabricValidation(matrixTypeComputed);

  // ---------------------------------------------------------------------------
  // Ownership verification
  // ---------------------------------------------------------------------------

  async function validateOwnership(
    metadata: TemplateMetadata,
  ): Promise<{ valid: false; reason: string } | { valid: true }> {
    // Check that the template was generated for the currently selected account
    if (metadata.accountId !== userStore.selectedAccount.id) {
      return { valid: false, reason: 'upload_error_wrong_account' };
    }

    // Verify the HMAC signature matches
    // Pass the template version so the server signs the same payload format.
    // Treat corrupted/non-numeric versions as tampered to avoid API validation errors.
    const rawVersion = metadata.version;
    if (rawVersion !== '1' && !/^\d+$/.test(rawVersion)) {
      return { valid: false, reason: 'upload_error_tampered' };
    }
    const version = rawVersion !== '1' ? rawVersion : undefined;
    const expectedSignature = await productsMatrixService.getTemplateSignature(userStore.selectedAccount.id, version);

    if (metadata.signature !== expectedSignature) {
      return { valid: false, reason: 'upload_error_tampered' };
    }

    return { valid: true };
  }

  // ---------------------------------------------------------------------------
  // Row validation
  // ---------------------------------------------------------------------------

  function validateRows(
    rows: ParsedExcelRow[],
    productMatrix: ProductMatrixApiResponse,
    seasonStartDate: string,
    seasonName: string,
    seasonEndDate?: string,
  ): ExcelUploadValidationResult {
    const isDistributor = userStore.accountIsDistributor();
    const productLookup = buildProductLookup(productMatrix.products);
    const successful: ValidatedRow[] = [];
    const failed: FailedRow[] = [];

    for (const row of rows) {
      const fabricProducts = productLookup.get(row.itemNumber);
      if (!fabricProducts) {
        failed.push({
          itemNumber: row.itemNumber,
          productGroupName: row.productGroupName,
          color: row.color,
          gender: row.gender,
          mainGroup: '',
          reasons: [t('excel_order_upload.validation_error.reasons.upload_product_not_found')],
        });

        continue;
      }

      const firstProduct = fabricProducts.values().next().value;
      const { sizeReasons, matchedProducts } = collectSizeReasons(row, fabricProducts, isDistributor);
      const reasons = [
        ...collectAssortmentReasons(firstProduct),
        ...collectDateReasons(row, isDistributor, seasonStartDate, seasonName, seasonEndDate),
        ...collectFabricAvailabilityReasons(firstProduct, row, seasonStartDate),
        ...sizeReasons,
      ];

      if (reasons.length > 0) {
        failed.push({
          itemNumber: row.itemNumber,
          productGroupName: row.productGroupName,
          color: row.color,
          gender: row.gender,
          mainGroup: firstProduct?.mainGroup ?? '',
          reasons,
        });
      } else {
        successful.push({ ...row, matchedProducts });
      }
    }

    return { successful, failed };
  }

  function collectAssortmentReasons(firstProduct: MatrixProduct | undefined): string[] {
    if (!firstProduct || isProductInSelectedAssortment(firstProduct.productGroupNumber)) {
      return [];
    }

    return [t('excel_order_upload.validation_error.reasons.upload_product_not_in_assortment')];
  }

  function isProductInSelectedAssortment(productGroupNumber: string): boolean {
    for (const name of productMatrixStore.selectedProductModelsNames) {
      const model = productMatrixStore.productModels.get(name);
      if (model?.itemNumbers?.includes(productGroupNumber)) {
        return true;
      }
    }

    return false;
  }

  function collectDateReasons(
    row: ParsedExcelRow,
    isDistributor: boolean,
    seasonStartDate: string,
    seasonName: string,
    seasonEndDate?: string,
  ): string[] {
    if (!row.requestedReceiptDate) {
      return [
        t(
          isDistributor
            ? 'excel_order_upload.validation_error.reasons.upload_exgo_date_missing'
            : 'excel_order_upload.validation_error.reasons.upload_requested_receipt_date_missing',
        ),
      ];
    }

    const reasons: string[] = [];

    if (isDistributor) {
      const exgoValidationWindow = getDistributorSeasonWindow(seasonName);
      if (exgoValidationWindow) {
        if (isDateBefore(row.requestedReceiptDate, exgoValidationWindow.earliest)) {
          reasons.push(t('excel_order_upload.validation_error.reasons.upload_dist_date_before_window'));
        }

        if (isDateAfter(row.requestedReceiptDate, exgoValidationWindow.latest)) {
          reasons.push(t('excel_order_upload.validation_error.reasons.upload_dist_date_after_window'));
        }

        return reasons;
      }

      // No season name provided — no range check for distributors.
      return [];
    }

    if (seasonStartDate && isDateBefore(row.requestedReceiptDate, seasonStartDate)) {
      reasons.push(t('excel_order_upload.validation_error.reasons.upload_date_before_season_start'));
    }

    if (seasonEndDate && isDateAfter(row.requestedReceiptDate, seasonEndDate)) {
      reasons.push(t('excel_order_upload.validation_error.reasons.upload_date_after_season_end'));
    }

    return reasons;
  }

  function collectFabricAvailabilityReasons(
    firstProduct: MatrixProduct | undefined,
    row: ParsedExcelRow,
    seasonStartDate: string,
  ): string[] {
    if (!firstProduct) return [];

    const orderDate = row.requestedReceiptDate ?? seasonStartDate;
    const disabledReason = getFabricDisabledReason(firstProduct.launchDate, orderDate, seasonStartDate);

    return disabledReason ? [t('excel_order_upload.validation_error.reasons.upload_product_not_available')] : [];
  }

  function collectSizeReasons(
    row: ParsedExcelRow,
    fabricProducts: Map<string, MatrixProduct>,
    isDistributor: boolean,
  ): { sizeReasons: string[]; matchedProducts: MatrixProduct[] } {
    const sizeReasons: string[] = [];
    const matchedProducts: MatrixProduct[] = [];

    for (const [sizeId, quantity] of Object.entries(row.quantities)) {
      const product = fabricProducts.get(sizeId);
      if (!product) {
        // Size no longer in the product matrix — skip silently so the rest of the row can still be processed.
        continue;
      }

      const minimumQuantity = getMinQuantity(isDistributor, product.mainGroup);
      if (minimumQuantity > 0 && quantity < minimumQuantity) {
        sizeReasons.push(
          t('excel_order_upload.validation_error.reasons.upload_below_minimum_quantity', {
            minimum: minimumQuantity,
          }),
        );

        continue;
      }

      if (quantity > MAX_QUANTITY) {
        sizeReasons.push(
          t('excel_order_upload.validation_error.reasons.upload_exceeds_maximum_quantity', {
            maximum: MAX_QUANTITY,
          }),
        );

        continue;
      }

      matchedProducts.push(product);
    }

    if (matchedProducts.length === 0 && sizeReasons.length === 0) {
      // All sizes were either unavailable or had no valid quantity — nothing to process.
      sizeReasons.push(t('excel_order_upload.validation_error.reasons.upload_no_processable_sizes'));
    }

    return { sizeReasons, matchedProducts };
  }

  return {
    validateOwnership,
    validateRows,
    groupByDateAndPO,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function groupByDateAndPO(result: ExcelUploadValidationResult): ExcelUploadGroupedResult {
  const groupMap = new Map<string, ExcelUploadOrderGroup>();

  for (const row of result.successful) {
    const dateStr = row.requestedReceiptDate!;
    const po = row.customerPoNumber || '';
    const key = `${dateStr}|||${po}`;

    let group = groupMap.get(key);
    if (!group) {
      group = {
        date: dateStr,
        poNumber: po,
        products: [],
      };
      groupMap.set(key, group);
    }

    group.products.push(row);
  }

  return {
    orders: Array.from(groupMap.values()),
    failed: result.failed,
  };
}

/**
 * Builds a lookup from productGroupNumber → Map<sizeId, MatrixProduct>.
 * This mirrors how `buildFabricRows` groups products in the download flow.
 */
function buildProductLookup(products: MatrixProduct[]): Map<string, Map<string, MatrixProduct>> {
  const lookup = new Map<string, Map<string, MatrixProduct>>();

  for (const product of products) {
    let sizeMap = lookup.get(product.productGroupNumber);
    if (!sizeMap) {
      sizeMap = new Map();
      lookup.set(product.productGroupNumber, sizeMap);
    }
    sizeMap.set(product.sizeId, product);
  }

  return lookup;
}

function getMinQuantity(isDistributor: boolean, mainGroup: string): number {
  if (!isDistributor) return 0;

  return DISTRIBUTOR_MOQ[mainGroup] ?? 0;
}
